import { createApi } from "@reduxjs/toolkit/query/react";
import { createBaseApiWithAuth } from "@/utilities/reduxToolkit";

/**
 * RTK Query slice for the Inbound "WORK IT" surface (REP-205 Phase 1).
 *
 * The inbound feed is the demand funnel inverted: one listing at the spine,
 * every interested party a lane. It is a UNION of two sources, merged by
 * person/email on the server:
 *
 *   (a) DERIVED "mirror" pursuits — buyers actively pursuing this exact
 *       property on their own side, found via the shared Property join. The
 *       pursuit STAGE is read LIVE from the buyer's ProjectProperty.status —
 *       never stored. source = "MIRROR".
 *   (b) STORED ListingInterest rows — slim rows for parties the lister
 *       PUSHED / INVITED off-platform or added MANUALly, and to home a broker
 *       note. source = "PUSH" | "INVITE" | "MANUAL".
 *
 * Every row is server-side ALLOWLISTED: counterparty BROKER identity + the
 * engagement facts that pertain to THIS property only. The buyer principal,
 * their ClientNeed budget/sqft/requirements, and their other pursuits never
 * leave the server. The client-side reveal() masking is presentation-only.
 *
 * Cross-tenant note: the stage/broker fields are read across tenants (the
 * lister looking at a different company's deal). A future REP-204 resolver
 * will gate that read server-side; the FE just consumes whatever the
 * allowlisted DTO returns.
 *
 * Backend (confirmed wiring):
 *   GET   /api/listings/:listingProjectId/inbound
 *   POST  /api/listings/:listingProjectId/inbound/manual
 *   PATCH /api/listings/:listingProjectId/inbound/:listingInterestId/note
 */

// ─── Enum mirrors ──────────────────────────────────────────────────────
// Keep in sync with src/dtos/listingInterest.dto.ts on the backend.

/** Stored sources. MIRROR is DERIVED (never persisted) but appears on the feed. */
export type InterestSource = "PUSH" | "INVITE" | "MANUAL";
export type InboundSource = InterestSource | "MIRROR";

/** Live pursuit stage read off the buyer's ProjectProperty.status. */
export type PursuitStage =
  | "TOURING"
  | "LOI"
  | "CONTRACT"
  | "DD"
  | "CLOSING"
  | "WON"
  | "PASSED";

/** The only counterparty identity ever disclosed — the public-facing broker. */
export interface InboundBroker {
  personId: string | null;
  name: string | null;
  brokerage: string | null;
  email: string | null;
  phone: string | null;
}

/**
 * A SELF-REPRESENTING pursuer's identity — surfaced only when there's no
 * broker on file (the principal is then the public-facing party). Allowlisted
 * to name + company; never contact details or the buyer's ClientNeed.
 */
export interface InboundPursuer {
  personId: string | null;
  name: string | null;
  brokerage: string | null;
}

/**
 * An ALLOWLISTED inbound row — the only shape the lister sees per party.
 * Mirrors InboundRowDto on the backend exactly.
 */
export interface InboundRow {
  /** Stable row identity (derived rows key on the buyer rep project; stored
   *  rows key on the ListingInterest id). */
  key: string;
  source: InboundSource;
  /** Live pursuit stage; null for stored rows with no derivable pursuit. */
  stage: PursuitStage | null;
  /** Zero-based stage index for swim-lane rendering; null when no pursuit. */
  stageIndex: number | null;
  toursOnThisProperty: number;
  hasLoi: boolean;
  loiStatus: string | null;
  broker: InboundBroker | null;
  /**
   * SELF-REPRESENTING pursuer identity — present (and `selfRepresenting` true)
   * only when there's no broker on file. The principal is the public-facing
   * party, so we show their name. Allowlisted: name + company only.
   */
  pursuer: InboundPursuer | null;
  /** True when this party has no broker and is representing itself. */
  selfRepresenting: boolean;
  brokerNote: string | null;
  firstSeenAt: string | null;
  lastActivityAt: string | null;
  /** The stored ListingInterest id, when this row is backed by one. */
  listingInterestId: string | null;
}

/** A read-on-demand radius match that is NOT yet inbound. */
export interface PotentialMatch {
  clientNeedId: string;
  buyerRepProjectId: string | null;
  broker: { id: string; name: string } | null;
  lookingFor: string;
  distanceMiles: number;
}

/** Aggregate funnel — counts only, no identities. */
export interface InboundFunnel {
  total: number;
  touring: number;
  loi: number;
  advanced: number;
  pushedOrInvited: number;
}

/** Full inbound response payload. */
export interface InboundView {
  listingProjectId: string;
  propertyId: string | null;
  inbound: InboundRow[];
  potential: PotentialMatch[];
  funnel: InboundFunnel;
}

export interface AddManualInterestBody {
  personId?: string;
  email?: string;
  brokerNote?: string;
}

export interface UpdateBrokerNoteBody {
  brokerNote?: string;
}

// A stored ListingInterest row (returned by manual-add / note mutations).
export interface ListingInterest {
  id: string;
  sellerRepProjectId?: string;
  propertyId?: string;
  personId?: string | null;
  email?: string | null;
  source?: InterestSource;
  brokerNote?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export const listingInterestApi = createApi({
  ...createBaseApiWithAuth({
    reducerPath: "listingInterestApi",
    baseUrl: "/api/listings",
  }),
  reducerPath: "listingInterestApi",
  tagTypes: ["Inbound"],
  endpoints: builder => ({
    /** The inbound "work it" view for a listing (seller/landlord rep project). */
    getInbound: builder.query<{ data: InboundView }, string>({
      query: listingProjectId => `/${listingProjectId}/inbound`,
      providesTags: (_r, _e, listingProjectId) => [
        { type: "Inbound" as const, id: listingProjectId },
      ],
    }),

    /** Lazily create a MANUAL ListingInterest row. */
    addManualInterest: builder.mutation<
      { data: ListingInterest },
      { listingProjectId: string; body: AddManualInterestBody }
    >({
      query: ({ listingProjectId, body }) => ({
        url: `/${listingProjectId}/inbound/manual`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { listingProjectId }) => [
        { type: "Inbound" as const, id: listingProjectId },
      ],
    }),

    /** Patch the broker note homed on a stored ListingInterest row. */
    updateBrokerNote: builder.mutation<
      { data: ListingInterest },
      {
        listingProjectId: string;
        listingInterestId: string;
        body: UpdateBrokerNoteBody;
      }
    >({
      query: ({ listingProjectId, listingInterestId, body }) => ({
        url: `/${listingProjectId}/inbound/${listingInterestId}/note`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { listingProjectId }) => [
        { type: "Inbound" as const, id: listingProjectId },
      ],
    }),
  }),
});

export const {
  useGetInboundQuery,
  useAddManualInterestMutation,
  useUpdateBrokerNoteMutation,
} = listingInterestApi;
